// utils/simplePerformance.js - Lightweight performance monitoring without circular dependencies
import logger from './logger';

class SimplePerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.isEnabled = process.env.NODE_ENV === 'development';
  }

  // Record a performance metric
  recordMetric(name, duration, metadata = {}) {
    if (!this.isEnabled) return;

    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }

    const entry = {
      duration,
      timestamp: Date.now(),
      ...metadata
    };

    const entries = this.metrics.get(name);
    entries.push(entry);

    // Keep only last 50 entries per metric
    if (entries.length > 50) {
      entries.shift();
    }

    // Log slow operations
    if (duration > 1000) {
      logger.performance(`${name} (SLOW)`, duration);
    } else if (duration > 100 && this.isEnabled) {
      logger.performance(name, duration);
    }
  }

  // Get statistics for a metric
  getStats(name) {
    const entries = this.metrics.get(name);
    if (!entries || entries.length === 0) return null;

    const durations = entries.map(e => e.duration);
    const sum = durations.reduce((a, b) => a + b, 0);
    const avg = sum / durations.length;
    const min = Math.min(...durations);
    const max = Math.max(...durations);

    return {
      count: durations.length,
      avg: Math.round(avg * 100) / 100,
      min: Math.round(min * 100) / 100,
      max: Math.round(max * 100) / 100,
      total: Math.round(sum * 100) / 100
    };
  }

  // Get all metrics
  getAllStats() {
    const stats = {};
    for (const [name] of this.metrics) {
      stats[name] = this.getStats(name);
    }
    return stats;
  }

  // Monitor memory usage (if available)
  checkMemory() {
    if (!this.isEnabled || !performance.memory) return null;

    const memory = performance.memory;
    const memoryInfo = {
      used: Math.round(memory.usedJSHeapSize / 1024 / 1024), // MB
      total: Math.round(memory.totalJSHeapSize / 1024 / 1024), // MB
      limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024) // MB
    };

    const usagePercent = (memoryInfo.used / memoryInfo.limit) * 100;
    if (usagePercent > 80) {
      logger.warn(`High memory usage: ${memoryInfo.used}MB (${usagePercent.toFixed(1)}%)`);
    }

    return memoryInfo;
  }

  // Generate a simple performance report
  generateReport() {
    if (!this.isEnabled) return { disabled: true };

    const stats = this.getAllStats();
    const memory = this.checkMemory();

    const report = {
      timestamp: new Date().toISOString(),
      memory,
      metrics: stats,
      summary: {
        totalMetrics: Object.keys(stats).length,
        slowestOperation: this.findSlowest(stats)
      }
    };

    logger.info('Performance Report:', report);
    return report;
  }

  // Find the slowest operation
  findSlowest(stats) {
    let slowest = null;
    let maxAvg = 0;

    for (const [name, stat] of Object.entries(stats)) {
      if (stat && stat.avg > maxAvg) {
        maxAvg = stat.avg;
        slowest = { name, avg: stat.avg };
      }
    }

    return slowest;
  }

  // Start basic monitoring
  startMonitoring() {
    if (!this.isEnabled) return;

    logger.info('Starting simple performance monitoring');

    // Check memory every 30 seconds
    if (typeof setInterval !== 'undefined') {
      setInterval(() => {
        this.checkMemory();
      }, 30000);
    }
  }
}

// Create singleton instance
const simplePerformanceMonitor = new SimplePerformanceMonitor();

// Auto-start in development
if (process.env.NODE_ENV === 'development') {
  simplePerformanceMonitor.startMonitoring();
}

export default simplePerformanceMonitor;

// Convenience functions
export const recordMetric = (name, duration, metadata) => 
  simplePerformanceMonitor.recordMetric(name, duration, metadata);

export const getStats = (name) => 
  simplePerformanceMonitor.getStats(name);

export const generateReport = () => 
  simplePerformanceMonitor.generateReport();

export const checkMemory = () => 
  simplePerformanceMonitor.checkMemory();